import { buildModifiedRows, prettyField } from './compareDrawer';
import { formatDateShort } from './dateFormat';

// Pure builder for the Variance Analysis PDF as a pdfmake document
// definition. Mirrors the visual language of BudgetPrintLayout (page 1 cost
// report) but produces REAL VECTOR TEXT — every glyph is drawn from an
// embedded TrueType font, so the resulting PDF is fully selectable,
// copyable and searchable. Contrast with VarianceAnalysisPrintLayout +
// html2pdf, which rasterises the DOM to a bitmap.
//
// This module has NO dependency on pdfmake itself: it only produces the
// declarative doc-def that pdfmake later renders. Keeping it pure means the
// layout is reasoned-about-as-data and unit-testable.

// ── Palette ────────────────────────────────────────────────────────────
const NAVY = '#1e3a5f';            // thead + grand total + header band
const NAVY_LIGHT = '#2c4870';      // section bands (Cost Codes / Labour)
const ZEBRA = '#f8f8f8';           // even-row stripe
const SUBSECTION_BG = '#f5f6f8';   // sub-section heading row
const SUBTOTAL_BG = '#f0f0f0';     // subtotal row
const SECONDARY_FG = '#666666';    // middle-version reference cells
const GREY_RULE = '#dddddd';
const RED = '#b91c1c';
const EMERALD = '#047857';
const MUTE = '#9ca3af';
const FOOTER_GREY = '#999999';

// ── Helpers ────────────────────────────────────────────────────────────
const sign = (v) => (v > 0 ? '+' : v < 0 ? '−' : '');
const impactColor = (v) => (v > 0 ? RED : v < 0 ? EMERALD : MUTE);
// Parameter is `fmt` not `formatMoney` because the regression scanner
// (server/tests/regression/client-fmt-scope-invariants.test.js) flags any
// top-level `formatMoney(...)` invocation. The lambda body only calls the
// passed-in formatter when `signed()` is invoked at runtime, but the
// scanner is purely lexical, so we alias.
const signed = (v, fmt) => `${sign(v)}${fmt(Math.abs(v))}`;
const DASH = '—';

const dateLong = () => new Date().toLocaleDateString('en-GB', {
  weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
});
const dateShort = () => new Date().toLocaleDateString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric',
});

// Build a row of cells with shared styling. Each `cells` item is either a
// string (becomes a text cell) or a partial cell object that's merged
// over the shared base.
function styledRow(cells, base = {}) {
  return cells.map((c) => {
    if (c == null) return { text: '', ...base };
    if (typeof c === 'string' || typeof c === 'number') return { text: c, ...base };
    return { ...base, ...c };
  });
}

/**
 * Build only the content[] slice for the Variance Analysis section. Used
 * when composing multi-section PDFs (e.g. Cost Report + Variance Analysis
 * in one document — see buildBudgetVariancePdf).
 */
export function buildVarianceAnalysisContent(args) {
  const doc = buildVarianceAnalysisDoc(args);
  return doc ? doc.content : [];
}

/**
 * Build a pdfmake document definition for the Variance Analysis report.
 *
 * @param {object} args
 * @param {object} args.compareData          /api/budget/versions/compare response
 * @param {object} args.reportData           /api/budget/report payload (versions + cost_codes)
 * @param {string} args.currentVersionName
 * @param {string} args.comparisonVersionName  baseline display name
 * @param {number} args.baselineVersionId
 * @param {string} args.departmentName
 * @param {string} args.productionName
 * @param {string} args.companyName
 * @param {boolean} args.includeVat
 * @param {boolean} args.includeUnchanged
 * @param {Function} args.formatMoney
 * @returns {object} pdfmake doc-def
 */
export function buildVarianceAnalysisDoc({
  compareData,
  reportData,
  currentVersionName,
  comparisonVersionName,
  baselineVersionId,
  // What word to use for the comparison-anchor column tag + subtitle.
  // Default is "Baseline" (Overview PDF semantics — anchor is the actual
  // original budget). The Compare pop-out PDF passes "Selected" because
  // the user can anchor against ANY past version, not just the oldest.
  baselineLabel = 'Baseline',
  departmentName,
  productionName,
  companyName,
  includeVat,
  includeUnchanged = false,
  formatMoney,
}) {
  if (!compareData) return null;

  // ── Version columns ──────────────────────────────────────────────────
  // Order matches BudgetPrintLayout exactly — Current first, then every
  // non-current version in the same natural array order the API returns.
  const versions = reportData?.versions || [];
  const currentVersion = versions.find(v => v.is_current);
  const prevVersionsAll = versions.filter(v => !v.is_current);
  const versionCols = [
    { kind: 'current', id: currentVersion?.id || 'current', name: currentVersionName, isBaseline: false },
    ...prevVersionsAll.map(v => ({
      kind: 'prev',
      id: v.id,
      name: v.version_name,
      isBaseline: v.id === baselineVersionId,
      meta: v,
    })),
  ];
  const colCount = 1 + versionCols.length + 1;

  // ── Per-version lookup helpers ───────────────────────────────────────
  const costCodesById = new Map((reportData?.cost_codes || []).map(c => [c.id, c]));
  const getCostCodeAmount = (codeId, versionId) => {
    const cc = costCodesById.get(codeId);
    if (!cc) return null;
    if (versionId === currentVersion?.id) {
      const v = Number(cc.budget_amount);
      return Number.isFinite(v) ? v : null;
    }
    const v = cc.version_budgets?.[versionId];
    return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
  };
  const crewPerVersion = compareData.crew_per_version || {};
  const getCrewVersionCost = (lineageId, versionId) => {
    if (lineageId == null) return null;
    const v = crewPerVersion[lineageId]?.[versionId];
    return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
  };

  // ── Variance totals ──────────────────────────────────────────────────
  const ccVariance = Number(compareData?.cost_codes?.total?.current_variance) || 0;
  const labourVariance = Number(compareData?.cost_delta?.current_variance) || 0;
  const grandVariance = ccVariance + labourVariance;

  // ── Per-section data ─────────────────────────────────────────────────
  const cc = compareData.cost_codes || { only_in_a: [], only_in_b: [], modified: [], unchanged: [] };
  const ccAdded = cc.only_in_a || [];
  const ccRemoved = cc.only_in_b || [];
  const ccModified = [...(cc.modified || [])].sort(
    (x, y) => Math.abs(Number(y.variance) || 0) - Math.abs(Number(x.variance) || 0),
  );
  const crewAdded = compareData.only_in_a || [];
  const crewRemoved = compareData.only_in_b || [];
  const crewModified = [...(compareData.modified || [])].sort((x, y) => {
    const xVar = Math.abs((Number(x.a?.budget_cost) || 0) - (Number(x.b?.budget_cost) || 0));
    const yVar = Math.abs((Number(y.a?.budget_cost) || 0) - (Number(y.b?.budget_cost) || 0));
    return yVar - xVar;
  });

  // ── Per-version aggregate helpers (subtotal + grand-total rows) ──────
  const ccTotalForCol = (col) => {
    if (col.kind === 'current') return Number(reportData?.grand_total?.purchases_budget) || 0;
    return Number(col.meta?.total_budget) || 0;
  };
  const labourTotalForCol = (col) => {
    if (col.kind === 'current') return Number(reportData?.grand_total?.labour_budget) || 0;
    return Number(col.meta?.crew_total) || 0;
  };
  const grandTotalForCol = (col) => ccTotalForCol(col) + labourTotalForCol(col);

  // ── Column widths — Item uses pdfmake's '*' star width to absorb
  // whatever space the page has left after the fixed numeric columns.
  // pdfmake handles the page-fitting maths internally; the table always
  // fills the page without overflowing the right margin, regardless of
  // version count.
  const VERSION_WIDTH = Math.max(56, Math.min(110, Math.floor(420 / versionCols.length)));
  const IMPACT_WIDTH = 110;
  const widths = ['*', ...versionCols.map(() => VERSION_WIDTH), IMPACT_WIDTH];
  // Used for the navy header underline below — fills the A4 landscape
  // content area (842 page − 2×28 margins = 786pt).
  const INNER_WIDTH = 786;

  // ── Build table body rows ────────────────────────────────────────────
  const body = [];

  // Header row — navy bg, white text. The Current column gets a small
  // "Current" sub-heading caption above the version name (stacked) so
  // the reader can tell at a glance which column is the live working
  // version. Other versions render as a single line (with the
  // "(Baseline)" suffix on the baseline column).
  body.push([
    { text: 'Item', fillColor: NAVY, color: 'white', bold: true, fontSize: 8 },
    ...versionCols.map(col => {
      if (col.kind === 'current') {
        return {
          stack: [
            { text: 'Current', fontSize: 6, bold: false, color: '#bcd0ea', alignment: 'center' },
            { text: col.name, fontSize: 8, bold: true, color: 'white', alignment: 'center' },
          ],
          fillColor: NAVY,
        };
      }
      return {
        text: col.isBaseline ? `${col.name} (${baselineLabel})` : col.name,
        fillColor: NAVY,
        color: 'white',
        bold: true,
        fontSize: 8,
        alignment: 'center',
      };
    }),
    { text: 'Impact', fillColor: NAVY, color: 'white', bold: true, fontSize: 8, alignment: 'right' },
  ]);

  // Row factories ───────────────────────────────────────────────────────
  // Section heading row. Sub-buckets (Newly budgeted / Budget removed /
  // Modified / Crew added / Crew removed) get their own subsectionRow
  // beneath this one so every bucket reads consistently.
  const sectionRow = (label) => [{
    text: label,
    colSpan: colCount,
    fillColor: NAVY_LIGHT,
    color: 'white',
    bold: true,
    fontSize: 11,
    margin: [4, 4, 4, 4],
    characterSpacing: 0.6,
  }, ...Array(colCount - 1).fill({})];

  const subsectionRow = (label) => [{
    text: label,
    colSpan: colCount,
    fillColor: SUBSECTION_BG,
    color: '#555555',
    italics: true,
    fontSize: 8,
    margin: [12, 2, 4, 2],
  }, ...Array(colCount - 1).fill({})];

  // Cost-code data row. `currentVal` and `baselineVal` may be null.
  const ccDataRow = (row, currentVal, baselineVal, impactVal) => {
    const cells = [
      // Item: code (mono small) + name
      { text: [{ text: row.code, bold: true, fontSize: 8 }, { text: ` ${row.name}`, fontSize: 8 }] },
    ];
    versionCols.forEach(col => {
      if (col.kind === 'current') {
        cells.push(currentVal == null
          ? { text: DASH, color: MUTE, alignment: 'right', fontSize: 8 }
          : { text: formatMoney(currentVal), alignment: 'right', fontSize: 8 });
        return;
      }
      if (col.id === baselineVersionId) {
        cells.push(baselineVal == null
          ? { text: DASH, color: MUTE, alignment: 'right', fontSize: 8 }
          : { text: formatMoney(baselineVal), alignment: 'right', fontSize: 8 });
        return;
      }
      const v = getCostCodeAmount(row.id, col.id);
      cells.push(v == null
        ? { text: DASH, color: MUTE, alignment: 'right', fontSize: 7 }
        : { text: formatMoney(v), color: SECONDARY_FG, alignment: 'right', fontSize: 7 });
    });
    cells.push({
      text: signed(impactVal, formatMoney),
      color: impactColor(impactVal),
      bold: impactVal !== 0,
      alignment: 'right',
      fontSize: 8,
    });
    return cells;
  };

  const crewDataRow = (row, currentVal, baselineVal, impactVal, lineageId, grade) => {
    const itemText = grade ? `${row.name} (${grade})` : row.name;
    const cells = [{ text: itemText, fontSize: 8 }];
    versionCols.forEach(col => {
      if (col.kind === 'current') {
        cells.push(currentVal == null
          ? { text: DASH, color: MUTE, alignment: 'right', fontSize: 8 }
          : { text: formatMoney(currentVal), alignment: 'right', fontSize: 8 });
        return;
      }
      if (col.id === baselineVersionId) {
        cells.push(baselineVal == null
          ? { text: DASH, color: MUTE, alignment: 'right', fontSize: 8 }
          : { text: formatMoney(baselineVal), alignment: 'right', fontSize: 8 });
        return;
      }
      const v = getCrewVersionCost(lineageId, col.id);
      cells.push(v == null
        ? { text: DASH, color: MUTE, alignment: 'right', fontSize: 7 }
        : { text: formatMoney(v), color: SECONDARY_FG, alignment: 'right', fontSize: 7 });
    });
    cells.push({
      text: signed(impactVal, formatMoney),
      color: impactColor(impactVal),
      bold: impactVal !== 0,
      alignment: 'right',
      fontSize: 8,
    });
    return cells;
  };

  // Per-field sub-row beneath a crew Modified summary. Sourced from the
  // shared buildModifiedRows helper so the PDF presents identical detail
  // to the on-screen Compare drawer (Mason Kowalski rules, slot grouping,
  // schedule-INPUTS muted, etc.).
  const IMPACT_HEX = { red: '#b91c1c', emerald: '#047857', mute: MUTE };
  const crewDetailRow = (subRow) => {
    const label = subRow.kind === 'slot' ? subRow.label : prettyField(subRow.field);
    const cells = [
      { text: label, fontSize: 7.5, italics: true, color: '#555555', margin: [12, 0, 0, 0] },
    ];
    versionCols.forEach(col => {
      if (col.kind === 'current') {
        cells.push({ text: subRow.aDisplay, fontSize: 7.5, color: '#555555', alignment: 'right' });
      } else if (col.id === baselineVersionId) {
        cells.push({ text: subRow.bDisplay, fontSize: 7.5, color: '#555555', alignment: 'right' });
      } else {
        // No per-field history across versions — dash everywhere else.
        cells.push({ text: DASH, fontSize: 7, color: MUTE, alignment: 'right' });
      }
    });
    cells.push({
      text: subRow.impact.text,
      fontSize: 7.5,
      bold: subRow.impact.tone !== 'mute',
      color: IMPACT_HEX[subRow.impact.tone] || MUTE,
      alignment: 'right',
    });
    return cells;
  };

  // Subtotal row — per-version totals + Impact
  const subtotalRow = (label, perColTotalFn, impactVal) => {
    const cells = [{
      text: label,
      fillColor: SUBTOTAL_BG,
      bold: true,
      fontSize: 9,
    }];
    versionCols.forEach(col => {
      cells.push({
        text: formatMoney(perColTotalFn(col)),
        fillColor: SUBTOTAL_BG,
        bold: true,
        alignment: 'right',
        fontSize: 9,
      });
    });
    cells.push({
      text: signed(impactVal, formatMoney),
      fillColor: SUBTOTAL_BG,
      color: impactColor(impactVal),
      bold: true,
      alignment: 'right',
      fontSize: 9,
    });
    return cells;
  };

  const grandRow = (label, perColTotalFn, impactVal) => {
    const cells = [{
      text: label,
      fillColor: NAVY,
      color: 'white',
      bold: true,
      fontSize: 10,
      margin: [2, 3, 2, 3],
    }];
    versionCols.forEach(col => {
      cells.push({
        text: formatMoney(perColTotalFn(col)),
        fillColor: NAVY,
        color: 'white',
        bold: true,
        alignment: 'right',
        fontSize: 10,
        margin: [2, 3, 2, 3],
      });
    });
    // Grand-total Impact uses LIGHTER red/emerald that stay readable on
    // navy. Matches the on-screen Compare drawer + FP&A industry norm
    // (Workday Adaptive / Procore / QuickBooks) where the bottom-line
    // variance is the strongest visual cue. White stays the fallback for
    // zero variance.
    const GRAND_IMPACT_RED = '#fca5a5';     // Tailwind red-300
    const GRAND_IMPACT_EMERALD = '#6ee7b7'; // Tailwind emerald-300
    const grandImpactColor = impactVal > 0
      ? GRAND_IMPACT_RED
      : impactVal < 0
        ? GRAND_IMPACT_EMERALD
        : 'white';
    cells.push({
      text: signed(impactVal, formatMoney),
      fillColor: NAVY,
      color: grandImpactColor,
      bold: true,
      alignment: 'right',
      fontSize: 10,
      margin: [2, 3, 2, 3],
    });
    return cells;
  };

  const emptyMessageRow = (msg) => [{
    text: msg,
    colSpan: colCount,
    italics: true,
    color: MUTE,
    fontSize: 8,
    margin: [4, 4, 4, 4],
  }, ...Array(colCount - 1).fill({})];

  // ── COST CODES ───────────────────────────────────────────────────────
  // Every bucket gets its own sub-heading with a "(N)" count so the
  // section structure reads consistently regardless of which buckets
  // happen to have data.
  body.push(sectionRow('Cost Codes'));
  if (ccAdded.length > 0) {
    body.push(subsectionRow(`Newly budgeted in ${currentVersionName} (${ccAdded.length})`));
    ccAdded.forEach(row => {
      body.push(ccDataRow(row, row.a_budget || 0, null, row.a_budget || 0));
    });
  }
  if (ccRemoved.length > 0) {
    body.push(subsectionRow(`Budget removed in ${currentVersionName} (${ccRemoved.length})`));
    ccRemoved.forEach(row => {
      body.push(ccDataRow(row, null, row.b_budget || 0, -(row.b_budget || 0)));
    });
  }
  if (ccModified.length > 0) {
    body.push(subsectionRow(`Modified (${ccModified.length})`));
    ccModified.forEach(row => {
      const v = Number(row.variance) || 0;
      body.push(ccDataRow(row, row.a_budget || 0, row.b_budget || 0, v));
    });
  }
  if (ccAdded.length + ccRemoved.length + ccModified.length === 0) {
    body.push(emptyMessageRow('No cost code budget changes between versions.'));
  }
  body.push(subtotalRow('Total Cost Code Variance', ccTotalForCol, ccVariance));

  // ── LABOUR ───────────────────────────────────────────────────────────
  body.push(sectionRow('Labour'));
  if (crewAdded.length > 0) {
    body.push(subsectionRow(`Crew added in ${currentVersionName} (${crewAdded.length})`));
    crewAdded.forEach(row => {
      body.push(crewDataRow(row, row.budget_cost || 0, null, row.budget_cost || 0, row.lineage_id, row.grade_name));
    });
  }
  if (crewRemoved.length > 0) {
    body.push(subsectionRow(`Crew removed in ${currentVersionName} (${crewRemoved.length})`));
    crewRemoved.forEach(row => {
      body.push(crewDataRow(row, null, row.budget_cost || 0, -(row.budget_cost || 0), row.lineage_id, row.grade_name));
    });
  }
  if (crewModified.length > 0) {
    body.push(subsectionRow(`Modified (${crewModified.length})`));
    crewModified.forEach(row => {
      const memberVar = (Number(row.a?.budget_cost) || 0) - (Number(row.b?.budget_cost) || 0);
      const grade = row.a?.grade_name || row.b?.grade_name;
      body.push(crewDataRow(row, row.a?.budget_cost || 0, row.b?.budget_cost || 0, memberVar, row.lineage_id, grade));
      // Per-field breakdown beneath the summary — same data + helpers the
      // on-screen Compare drawer uses, so the PDF shows WHY each cost
      // changed (Start/End date, Total weeks, Wage override, Allowances).
      const detailRows = buildModifiedRows(row, { formatMoney, formatDateShort });
      detailRows.forEach(sub => body.push(crewDetailRow(sub)));
    });
  }
  if (crewAdded.length + crewRemoved.length + crewModified.length === 0) {
    body.push(emptyMessageRow('No labour changes between versions.'));
  }
  body.push(subtotalRow('Total Labour Variance', labourTotalForCol, labourVariance));

  // ── GRAND TOTAL ──────────────────────────────────────────────────────
  body.push(grandRow('GRAND TOTAL VARIANCE', grandTotalForCol, grandVariance));

  // ── Optional Unchanged appendix ──────────────────────────────────────
  const content = [
    // Header band — title on the left, dept/date right-aligned on the
    // SAME row. Saves a row vs stacking them. The right column's small
    // top margin visually aligns the dept-line baseline with the 18pt
    // title baseline.
    {
      columns: [
        {
          width: '*',
          text: (() => {
            const subtitle = productionName || companyName;
            // Lead with the department so the report self-identifies, e.g.
            // "SFX Variance Analysis — PROJECT AURORA" — mirrors the Cost
            // Report header (budgetReportPdf.js).
            const titleText = `${departmentName ? `${departmentName} ` : ''}Variance Analysis`;
            if (!subtitle) return titleText;
            return [
              { text: titleText, fontSize: 18, bold: true },
              { text: `  —  ${subtitle.toUpperCase()}`, fontSize: 13, bold: true },
            ];
          })(),
          color: NAVY,
        },
        {
          // Just the date — the department now lives in the title on the left,
          // so repeating it here would duplicate it on the same row.
          width: 'auto',
          text: dateLong().toUpperCase(),
          fontSize: 10, color: '#555555', alignment: 'right',
          margin: [0, 9, 0, 0],
        },
      ],
      margin: [0, 0, 0, 2],
    },
    // Standalone company line only when both production AND company exist.
    productionName && companyName && {
      text: companyName.toUpperCase(),
      fontSize: 11,
      bold: true,
      color: '#333333',
      margin: [0, 0, 0, 1],
    },
    // Subtitle band — 3pt navy underline + italic line
    {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: INNER_WIDTH, y2: 0, lineWidth: 2, lineColor: NAVY }],
      margin: [0, 0, 0, 4],
    },
    {
      text: `Comparing ${currentVersionName} against ${comparisonVersionName} (${baselineLabel})${includeVat !== undefined ? ` · ${includeVat ? 'incl. VAT' : 'ex. VAT'}` : ''}`,
      italics: true,
      color: '#666666',
      fontSize: 9,
      margin: [0, 0, 0, 8],
    },
    // Main table
    {
      table: { headerRows: 1, widths, body, dontBreakRows: true },
      layout: {
        fillColor: (rowIndex, _node) => {
          // Zebra only for data rows. Header (0) handled by explicit fillColor.
          // Skip zebra when the row already has its own fillColor — pdfmake
          // respects cell-level fillColor over the layout function, so this
          // is just a default for plain data rows.
          if (rowIndex === 0) return null;
          return rowIndex % 2 === 0 ? null : ZEBRA;
        },
        hLineColor: () => GREY_RULE,
        vLineColor: () => GREY_RULE,
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        paddingTop: () => 2,
        paddingBottom: () => 2,
        paddingLeft: () => 4,
        paddingRight: () => 4,
      },
    },
  ].filter(Boolean);

  if (includeUnchanged) {
    const unchangedBody = [
      [
        { text: 'Item', fillColor: NAVY, color: 'white', bold: true, fontSize: 8 },
        { text: 'Cost', fillColor: NAVY, color: 'white', bold: true, fontSize: 8, alignment: 'right' },
      ],
    ];
    const ccUnchanged = cc.unchanged || [];
    const crewUnchanged = compareData.unchanged || [];
    if (ccUnchanged.length > 0) {
      unchangedBody.push([
        { text: 'Cost Codes', colSpan: 2, fillColor: NAVY_LIGHT, color: 'white', bold: true, fontSize: 9, margin: [4, 3, 4, 3] },
        {},
      ]);
      ccUnchanged.forEach(row => {
        unchangedBody.push([
          { text: [{ text: row.code, bold: true, fontSize: 8 }, { text: ` ${row.name}`, fontSize: 8 }] },
          { text: formatMoney(row.a_budget || 0), alignment: 'right', fontSize: 8 },
        ]);
      });
    }
    if (crewUnchanged.length > 0) {
      unchangedBody.push([
        { text: 'Labour', colSpan: 2, fillColor: NAVY_LIGHT, color: 'white', bold: true, fontSize: 9, margin: [4, 3, 4, 3] },
        {},
      ]);
      crewUnchanged.forEach(row => {
        unchangedBody.push([
          { text: row.grade_name ? `${row.name} (${row.grade_name})` : row.name, fontSize: 8 },
          { text: formatMoney(row.budget_cost || 0), alignment: 'right', fontSize: 8 },
        ]);
      });
    }
    if (ccUnchanged.length === 0 && crewUnchanged.length === 0) {
      unchangedBody.push([
        { text: 'No unchanged items.', colSpan: 2, italics: true, color: MUTE, fontSize: 8 },
        {},
      ]);
    }
    content.push(
      {
        // Always start the Unchanged appendix on a fresh page so the
        // primary variance content (Cost Codes + Labour + Grand Total)
        // isn't visually cluttered by the long list of unchanged rows.
        text: `Unchanged items — identical between ${currentVersionName} and ${comparisonVersionName}`,
        italics: true,
        color: '#666666',
        fontSize: 9,
        margin: [0, 0, 0, 4],
        pageBreak: 'before',
      },
      {
        // Use the same '*' star-width strategy as the main variance table
        // so the Unchanged appendix has identical total width — pdfmake
        // auto-fits the description column to the page after the fixed
        // Cost column. Cost column is sized to match the Impact column on
        // the main table for visual continuity.
        table: { headerRows: 1, widths: ['*', IMPACT_WIDTH], body: unchangedBody },
        layout: {
          fillColor: (rowIndex) => (rowIndex > 0 && rowIndex % 2 === 0 ? ZEBRA : null),
          hLineColor: () => GREY_RULE,
          vLineColor: () => GREY_RULE,
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          paddingTop: () => 2,
          paddingBottom: () => 2,
          paddingLeft: () => 4,
          paddingRight: () => 4,
        },
      },
    );
  }

  // ── Document definition ──────────────────────────────────────────────
  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [28, 32, 28, 36],
    info: {
      title: `${departmentName} Variance Analysis`,
      author: companyName || 'Crew Forge',
      subject: `Variance Analysis — ${currentVersionName} vs ${comparisonVersionName}`,
    },
    footer: (currentPage, pageCount) => ({
      columns: [
        {
          text: `Printed on ${dateShort()} | ${departmentName || ''} Variance Analysis | Crew Forge`,
          fontSize: 7,
          color: FOOTER_GREY,
          alignment: 'left',
          margin: [28, 8, 0, 0],
        },
        {
          text: `Page ${currentPage} of ${pageCount}`,
          fontSize: 7,
          color: FOOTER_GREY,
          alignment: 'right',
          margin: [0, 8, 28, 0],
        },
      ],
    }),
    defaultStyle: { fontSize: 9, color: '#000000' },
    content,
  };
}
